/** biome-ignore-all lint/suspicious/noEmptyBlockStatements: no empty blocks */
import {useCallback, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  useDeleteHostPhotosMutation,
  useDeletePhotosMutation,
} from '~/ViewInvitation/api/endpoints/photos';
import {invalidateTag} from '~/ViewInvitation/api/photos';
import {selectEventId} from '~/ViewInvitation/selectors/eventDetails';
import {selectGuestId, selectUserId} from '~/ViewInvitation/selectors/user';
import {toastAlert} from '~library/atoms/Toast';
import {isHostOrCF} from '../utils';

export const useDeletePhoto = ({
  photos,
  photoCount,
  onClose = () => {},
  setShowingDeleteMessage = (_arg) => {},
  setShowingContextMenu = () => {},
  currentIndex = 0,
  isHostModule = false,
  isPhotoViewer = false,
  setHasUploadedNoTitle = () => undefined,
}) => {
  const dispatch = useDispatch();
  const guestId = useSelector(selectGuestId);
  const userId = useSelector(selectUserId);
  const eventId = useSelector(selectEventId);
  const [showingFlagMessage, setShowingFlagMessage] = useState(false);
  const [deletePhoto] = useDeletePhotosMutation({
    fixedCacheKey: 'shared-delete-photos',
  });
  const [deleteHostPhotos] = useDeleteHostPhotosMutation({
    fixedCacheKey: 'host-delete-photos',
  });

  const canDeletePhoto = useCallback(
    (currentImage) =>
      currentImage.creator?.id === userId || currentImage.guest_id === guestId || isHostOrCF(),
    [guestId, userId]
  );

  const handleDelete = useCallback(() => {
    setShowingDeleteMessage(false);
    setShowingContextMenu(false);
  }, [setShowingDeleteMessage, setShowingContextMenu]);

  const deleteCurrentPhoto = useCallback(
    async (_, photoIdToDelete) => {
      try {
        const photoId = photoIdToDelete || photos[currentIndex]?.id;
        if (isHostModule) {
          await deleteHostPhotos({
            eventId,
            ids: [photoId],
          });
        } else {
          await deletePhoto({
            guestId,
            eventId,
            id: photoId,
          });
        }

        if (isPhotoViewer) {
          const nextCount = photoCount - 1;
          await dispatch(invalidateTag(isHostModule));
          if (nextCount === 0) {
            handleDelete();
            onClose();
          } else {
            handleDelete();
          }
        } else {
          dispatch(invalidateTag(isHostModule));
          handleDelete(isHostModule);
        }
      } catch (e) {
        evite.error('Failed to delete photo', e);
        toastAlert('Failed to delete photo', {type: 'error'});
      }
    },
    [
      guestId,
      eventId,
      photos,
      currentIndex,
      photoCount,
      isHostModule,
      isPhotoViewer,
      deletePhoto,
      deleteHostPhotos,
      dispatch,
      handleDelete,
      onClose,
    ]
  );

  const deleteAllPhotos = useCallback(async () => {
    try {
      if (isHostModule) {
        const ids = photos.map((photo) => photo.id);
        setHasUploadedNoTitle(false);
        await deleteHostPhotos({eventId, ids}).unwrap();
      } else {
        await Promise.allSettled(
          photos.map((photo) => deletePhoto({guestId, eventId, id: photo.id}))
        );
      }

      dispatch(invalidateTag(isHostModule));
    } catch (e) {
      evite.error('Failed to delete all photos', e);
      toastAlert('Failed to delete all photos', {type: 'error'});
    }
  }, [isHostModule, photos, guestId, eventId, deletePhoto, deleteHostPhotos, dispatch]);

  return {
    canDeletePhoto,
    deleteCurrentPhoto,
    showingFlagMessage,
    setShowingFlagMessage,
    deleteAllPhotos,
  };
};
